import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';

import HomeCarousel from '../home/HomeCarousel';
import i18n from '../../i18n';
import Api from '../../api/Api';
import Search from '../../api/Search';
import { toggleFavorite } from '../../actions/homeFavorites';
import {
    Loader,
    Flex,
    Section,
    Container,
    Title,
    IconButton,
    Carousel,
    CarouselItem
} from '@homeexchange/design';
import { HomeCard } from '../search/results-ts/homecard';
import Home from '../../models/Home';

class HomepageNewest extends React.Component {
    static propTypes = {
        homes: PropTypes.array,
        isLogged: PropTypes.bool,
        isSimilarHome: PropTypes.bool,
        homeId: PropTypes.number,
        accessToken: PropTypes.string,
        refreshToken: PropTypes.string,
        dispatch: PropTypes.func,
        homeType: PropTypes.string,
        userId: PropTypes.number,
        user: PropTypes.object,
        homeFavorites: PropTypes.array
    };

    constructor(props) {
        super(props);
        this.state = {
            homes: props.homes || [],
            hasFetched: false,
            isLoading: false,
            navigationState: {
                shouldDisplayNext: false,
                shouldDisplayPrev: false
            }
        };
        this.moduleRef = React.createRef();
        this.carouselRef = React.createRef();
        this.fetchHomes = this.fetchHomes.bind(this);
        this.fetchSimilarHomes = this.fetchSimilarHomes.bind(this);
        this.handleToggleFavorite = this.handleToggleFavorite.bind(this);
        this.setNavigationState = this.setNavigationState.bind(this);
    }

    UNSAFE_componentWillMount() {
        if (!this.props.homes) {
            if (this.props.isSimilarHome) {
                if (this.props.homeType) {
                    this.fetchSimilarHomes();
                }
            } else {
                this.fetchHomes();
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.isSimilarHome &&
            !this.state.hasFetched &&
            !this.props.homes &&
            this.props.homeType &&
            prevProps.homeType !== this.props.homeType
        ) {
            this.setState({ isLoading: true });
            this.fetchSimilarHomes();
        }
    }

    fetchHomes() {
        const locale = document.documentElement.lang || 'en';
        Api.Home.getLastCompletedHomesWithCompletedUserProfile(20, locale)
            .done((response) => {
                this.setState({
                    homes: response,
                    error: response.length > 0 ? null : i18n.t('dashboard:noresults')
                });
            })
            .fail(() => {
                this.setState({
                    error: i18n.t('dashboard:noresults')
                });
            });
    }

    async fetchSimilarHomes() {
        const { accessToken, refreshToken, dispatch, homeType, userId, homeId } = this.props;

        if (!homeType) {
            this.setState({ isLoading: false });
            return;
        }

        if (this.state.hasFetched) {
            return;
        }

        this.setState({ hasFetched: true });

        try {
            const filters = {
                filters: ['home-with-picture'],
                near: {
                    home_id: homeId,
                    include_distance: '40km'
                },
                calendar: {
                    date_range: [
                        {
                            from: moment().format('YYYY-MM-DD'),
                            to: moment().add(6, 'month').format('YYYY-MM-DD')
                        }
                    ]
                },
                home: {
                    type: homeType === 1 ? 'home' : 'flat'
                },
                exclude_user: userId
            };

            const data = await Search.sendRequest(
                filters,
                0,
                20,
                null,
                null,
                null,
                accessToken,
                refreshToken,
                dispatch,
                false
            );

            this.setState({
                homes: data.homes.map((home) => new Home(home)),
                error: data.homes.length > 0 ? null : i18n.t('dashboard:noresults'),
                isLoading: false
            });
        } catch (error) {
            this.setState({
                error: i18n.t('dashboard:noresults'),
                isLoading: false
            });
        }
    }

    handleNextClick = () => {
        if (this.carouselRef.current) {
            this.carouselRef.current.scrollNext();
        }
    };

    handlePrevClick = () => {
        if (this.carouselRef.current) {
            this.carouselRef.current.scrollPrev();
        }
    };

    handleToggleFavorite(homeId) {
        const homeFavorites = this.props.homeFavorites || [];
        this.props.dispatch(toggleFavorite(homeId, homeFavorites));

        if (homeFavorites.includes(homeId)) {
            this.props.dispatch({
                type: 'REMOVE_HOME_FAVORITE',
                homeFavorites: homeId
            });
        } else {
            this.props.dispatch({
                type: 'ADD_HOME_FAVORITE',
                homeFavorites: homeId
            });
        }
    }

    setNavigationState = (navigationState) => {
        if (
            navigationState.shouldDisplayNext !== this.state.navigationState.shouldDisplayNext ||
            navigationState.shouldDisplayPrev !== this.state.navigationState.shouldDisplayPrev
        ) {
            this.setState({ navigationState });
        }
    };

    render() {
        const moduleClasses = classNames({
            'home-newest-houses': true,
            'home-newest-houses-loading': this.state.homes.length === 0 && !this.state.error
        });
        const { isSimilarHome } = this.props;
        const locale = i18n.language;

        if (this.state.isLoading) {
            return (
                <Flex justifyContent="center" alignItems="center" isFullWidth style={{ height: '200px' }}>
                    <Loader size="medium" />
                </Flex>
            );
        }

        if (isSimilarHome && this.state.homes.length > 0 && !this.state.isLoading) {
            return (
                <div data-theme="regular">
                    <Section color="secondary">
                        <Container>
                            <Flex gap="8">
                                <Flex
                                    direction="row"
                                    gap="1"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    isFullWidth
                                    wrap="nowrap"
                                >
                                    <Flex direction="column" gap="1">
                                        <Title subtitle={i18n.t(`home:similar.homes.subtitle`)}>
                                            {i18n.t(`home:similar.homes.title`)}
                                        </Title>
                                    </Flex>
                                    <Flex direction="row" gap="4" hideOnMobile wrap="nowrap">
                                        <IconButton
                                            icon="arrow-thin"
                                            size="medium"
                                            aria-label="Previous"
                                            onClick={this.handlePrevClick}
                                            disabled={!this.state.navigationState.shouldDisplayPrev}
                                        />
                                        <IconButton
                                            icon="arrow-thin-right"
                                            size="medium"
                                            aria-label="Next"
                                            onClick={this.handleNextClick}
                                            disabled={!this.state.navigationState.shouldDisplayNext}
                                        />
                                    </Flex>
                                </Flex>
                                <Carousel
                                    ref={this.carouselRef}
                                    onNavigationStateChange={this.setNavigationState}
                                    itemsPerBreakpoint={{
                                        xs: { items: 1, peek: 15 },
                                        sm: { items: 1, peek: 15 },
                                        md: { items: 2, peek: 20 },
                                        lg: { items: 3, peek: 0 },
                                        xl: { items: 4, peek: 0 }
                                    }}
                                >
                                    {this.state.homes.map((home) => (
                                        <CarouselItem key={home.get ? home.get('id') : home.id}>
                                            <HomeCard
                                                result={home}
                                                locale={locale}
                                                isLogged={this.props.isLogged}
                                                isUserCollection={this.props.user?.get('is_he_collection')}
                                                homeFavorites={this.props.homeFavorites}
                                                handleToggleFavorite={this.handleToggleFavorite}
                                                format="list"
                                                hideFixedDates={false}
                                                user={this.props.user}
                                                trackingEvent={'home_view_carousel_suggestion'}
                                                origin={'home_view_similar_carousel'}
                                            />
                                        </CarouselItem>
                                    ))}
                                </Carousel>
                            </Flex>
                        </Container>
                    </Section>
                </div>
            );
        }

        if (this.state.error || this.state.homes.length === 0) {
            return false;
        }

        return (
            <div ref={this.moduleRef} className={moduleClasses}>
                <div className="container">
                    <div className="row">
                        <div className={`col-md-${isSimilarHome ? '12' : '10 col-md-offset-1'}`}>
                            <h2>{i18n.t(`home:${isSimilarHome ? 'similar' : 'homepage'}.homes.title`)}</h2>
                            <p>{i18n.t(`home:${isSimilarHome ? 'similar' : 'homepage'}.homes.subtitle`)}</p>
                            <HomeCarousel
                                homes={this.state.homes.slice(0, 20)}
                                fakes={20}
                                displayFavoritePlaceholder={false}
                                isLogged={this.props.isLogged}
                                noSearch={true}
                            />
                            {!isSimilarHome && (
                                <div className="homepage-homes-cta">
                                    <a
                                        href={i18n.t('search:search_url', {
                                            slug: 'everywhere'
                                        })}
                                        className="btn btn-primary "
                                    >
                                        {i18n.t('home:homepage.homes.cta')}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const { homeId } = ownProps;

    const home = state.loadedHomes[homeId] || null;

    return {
        accessToken: state.auth.accessToken,
        refreshToken: state.auth.refreshToken,
        user: state.user,
        homeFavorites: state.homeFavorites || [],
        homeType: home?.get('type')
    };
};

const mapDispatchToProps = (dispatch) => ({
    dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(HomepageNewest);
